import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import { messagingService } from '../../services/messagingService';

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef(null);
  const subscriptionRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    
    // Subscribe to real-time notification updates
    const setupSubscription = async () => {
      subscriptionRef.current = await messagingService?.subscribeToNotifications((newNotification) => {
        loadNotifications();
      });
    };
    
    setupSubscription();

    return () => {
      if (subscriptionRef?.current) {
        subscriptionRef?.current?.unsubscribe();
      }
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setIsLoading(true);
      const data = await messagingService?.getUserNotifications();
      setNotifications(data);
      const unread = data?.filter(n => !n?.read)?.length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 1000 / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) {
      return 'Şimdi';
    } else if (minutes < 60) {
      return `${minutes} dakika önce`;
    } else if (hours < 24) {
      return `${hours} saat önce`;
    } else if (days === 1) {
      return 'Dün';
    } else if (days < 7) {
      return `${days} gün önce`;
    } else {
      return date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    }
  };

  const getNotificationStyle = (type, priority) => {
    const styles = {
      weather: {
        bg: 'bg-orange-50',
        icon: 'bg-orange-100 text-orange-600',
        border: 'border-orange-200'
      },
      waitlist: {
        bg: 'bg-green-50',
        icon: 'bg-green-100 text-green-600',
        border: 'border-green-200'
      },
      message: {
        bg: 'bg-blue-50',
        icon: 'bg-blue-100 text-blue-600',
        border: 'border-blue-200'
      },
      booking: {
        bg: 'bg-purple-50',
        icon: 'bg-purple-100 text-purple-600',
        border: 'border-purple-200'
      },
      system: {
        bg: 'bg-gray-50',
        icon: 'bg-gray-100 text-gray-600',
        border: 'border-gray-200'
      },
      badge: {
        bg: 'bg-yellow-50',
        icon: 'bg-yellow-100 text-yellow-600',
        border: 'border-yellow-200'
      }
    };
    return styles?.[type] || styles?.system;
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification?.read) {
        await messagingService?.markAsRead(notification?.id);
        setNotifications(prev =>
          prev?.map(n =>
            n?.id === notification?.id ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      if (notification?.actionUrl) {
        window.location.href = notification?.actionUrl;
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await messagingService?.markAllAsRead();
      setNotifications(prev => prev?.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await messagingService?.deleteAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      setFilter('all');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e?.stopPropagation();
    try {
      await messagingService?.deleteNotification(notificationId);
      setNotifications(prev => {
        const filtered = prev?.filter(n => n?.id !== notificationId);
        const unread = filtered?.filter(n => !n?.read)?.length;
        setUnreadCount(unread);
        return filtered;
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : notifications?.filter(n => n?.type === filter);

  const filterOptions = [
    { value: 'all', label: 'Tümü', icon: 'Inbox' },
    { value: 'weather', label: 'Hava Durumu', icon: 'CloudRain' },
    { value: 'waitlist', label: 'Bekleme Listesi', icon: 'UserPlus' },
    { value: 'message', label: 'Mesajlar', icon: 'MessageCircle' },
    { value: 'booking', label: 'Rezervasyonlar', icon: 'Calendar' },
    { value: 'badge', label: 'Rozetler', icon: 'Award' },
    { value: 'system', label: 'Sistem', icon: 'Settings' }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-all duration-200 group"
        aria-label="Bildirimler"
      >
        <Icon name="Bell" size={20} color="#374151" className="group-hover:scale-110 transition-transform" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="fixed inset-0 sm:absolute sm:inset-auto sm:right-0 sm:mt-2 sm:w-[420px] bg-white sm:rounded-xl shadow-2xl border border-gray-200 z-50 sm:max-h-[650px] flex flex-col overflow-hidden mt-0 top-[60px] sm:top-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-white">Bildirimler</h3>
                <p className="text-xs text-blue-100 mt-0.5">
                  {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Tüm bildirimler okundu'}
                </p>
              </div>
              {notifications?.length > 0 && (
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs text-white hover:text-blue-100 font-medium bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors"
                      title="Tümünü okundu işaretle"
                    >
                      <Icon name="CheckCheck" size={14} />
                    </button>
                  )}
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-white hover:text-blue-100 bg-white/20 hover:bg-white/30 p-1.5 rounded-lg transition-colors"
                    title="Tümünü temizle"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              {filterOptions?.map((option) => {
                const count = option?.value === 'all' 
                  ? notifications?.length 
                  : notifications?.filter(n => n?.type === option?.value)?.length;
                
                if (count === 0 && option?.value !== 'all') return null;
                
                return (
                  <button
                    key={option?.value}
                    onClick={() => setFilter(option?.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                      filter === option?.value
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    <Icon name={option?.icon} size={12} />
                    <span>{option?.label}</span>
                    {count > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                        filter === option?.value ? 'bg-blue-100 text-blue-600' : 'bg-white/30 text-white'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1 bg-gray-50">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredNotifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4 shadow-inner">
                  <Icon name="Bell" size={36} color="#9CA3AF" />
                </div>
                <p className="text-gray-600 text-sm font-semibold">Bildirim bulunmuyor</p>
                <p className="text-gray-400 text-xs mt-1.5 text-center max-w-[200px]">
                  {filter === 'all' ? 'Yeni bildirimler burada görünecek' : 'Bu kategoride bildirim yok'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredNotifications?.map((notification) => {
                  const style = getNotificationStyle(notification?.type, notification?.priority);
                  return (
                    <div
                      key={notification?.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`relative px-4 py-3.5 hover:bg-white cursor-pointer transition-all duration-200 group ${
                        !notification?.read ? 'bg-white border-l-4 ' + style?.border : 'bg-gray-50/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center shadow-sm ${style?.icon}`}>
                          <Icon name={notification?.icon} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className={`text-sm font-semibold ${
                              !notification?.read ? 'text-gray-900' : 'text-gray-600'
                            }`}>
                              {notification?.title}
                            </p>
                            {!notification?.read && (
                              <span className="flex-shrink-0 w-2.5 h-2.5 bg-blue-600 rounded-full mt-1 shadow-sm"></span>
                            )}
                          </div>
                          <p className={`text-sm leading-relaxed ${
                            !notification?.read ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification?.message}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-400 font-medium flex items-center gap-1">
                              <Icon name="Clock" size={11} />
                              {formatTimestamp(notification?.timestamp)}
                            </p>
                            {notification?.priority === 'high' && (
                              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                ÖNEMLİ
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification?.id)}
                          className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition-all"
                          title="Sil"
                        >
                          <Icon name="X" size={14} color="#EF4444" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="border-t border-gray-200 bg-white px-4 py-3">
              <button className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-semibold hover:bg-blue-50 py-2 rounded-lg transition-colors">
                Tüm Bildirimleri Görüntüle
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;